import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import {
  FileToDownload,
  GetDocumentListPayload,
  GetDocumentPayload,
} from '@/domains/documents/store/types/DocumentsSlice';
import { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsDynamicOverrideMode } from '@/features/eds/enums/EdsDynamicOverrideMode';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { AllClaimSetsSlots } from '@/domains/claimSets/enums/AllClaimSetsSlots';
import {
  downloadDocument,
  getCorrespondenceStatusHistory,
  getCorrespondenceStatusHistorySuccess,
  getDocument,
  getDocumentEntityVersions,
  getDocumentEntityVersionsSuccess,
  getDocumentList,
  getDocumentListDataError,
  getDocumentListSuccess,
  getDocumentMetadata,
  getDocumentMetadataSuccess,
  getDocumentStatusHistory,
  getDocumentStatusHistorySuccess,
  getDocumentSuccess,
  getDocumentWithCorrespondence,
  getDocumentWithCorrespondenceSuccess,
  incrementDocumentsPage,
  setCorrespondenceStatusHistoryLoading,
  setDocumentMetadataLoading,
  setDocumentStatusHistoryLoading,
  setDocumentVersionsLoading,
  setDocumentWithCorrespondencesLoading,
  startLoadingDocumentListData,
} from '@/domains/documents/store/slices/documentsSlice/documentsSlice';
import { SortDirection } from '@/enums/SortDirection';
import { DocumentService } from '@/services/DocumentService/DocumentService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import { documentVersionsOverride } from './utils/documentEntityVersionsOverride';
import { limitDocumentCreatedByAccounts } from './utils/limitDocumentCreatedByAccounts';

const DOCUMENT_INCLUDES = [
  `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.DocumentVersions}.${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
  `${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
  `${EdsEntityCodes.DocumentType}`,
].join(',');

const DOCUMENT_WITH_CORRESPONDENCE_INCLUDES = [
  `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.CorrespondenceItems}.${EdsEntityCodes.CorrespondenceSet}.${EdsEntityCodes.Correspondences}.${EdsEntityCodes.Party}.${EdsEntityCodes.Person}`,
  `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.CorrespondenceItems}.${EdsEntityCodes.CorrespondenceSet}.${EdsEntityCodes.Correspondences}.${EdsEntityCodes.Party}.${EdsEntityCodes.Business}`,
  `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.CorrespondenceItems}.${EdsEntityCodes.CorrespondenceSet}.${EdsEntityCodes.Correspondences}.${EdsEntityCodes.MailingAddress}`,
].join(',');

export function* getEDSDocumentListSaga({ type, payload }: PayloadAction<GetDocumentListPayload>) {
  yield put(startLoadingDocumentListData());
  try {
    const pagination = yield select((state: RootState) => state.documents.data.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.Document}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      sortBy: edsToEntityFormat('createdAt'),
      sortDirection: SortDirection.DESC,
      includes: DOCUMENT_INCLUDES,
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, payload?.filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      dynamicOverrideMode: EdsDynamicOverrideMode.Merge,
      section: EdsDefinitionSections.SidePanel,
    });

    const transformedDataWithLatestVersion: NormalizedV2Response[] = yield call(
      Promise.all.bind(Promise),
      normalizedResponse.map(async (normalizedItem, index) => {
        return DocumentService.mergeLastInstanceVersionIntoDocumentFields(
          normalizedItem,
          data.listings[index].documentInstances,
          EdsDefinitionSections.SidePanel,
        );
      }),
    );

    const transformedDataWithSingleAccount = limitDocumentCreatedByAccounts(transformedDataWithLatestVersion);

    yield put(
      getDocumentListSuccess({
        shouldRewrite: type !== incrementDocumentsPage.type,
        data: {
          listings: transformedDataWithSingleAccount,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Documents fetching error',
      content: 'Unable to fetch documents. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(getDocumentListDataError());
  }
}

function* getEDSDocumentSaga({ payload: { id } }: PayloadAction<GetDocumentPayload>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/${EdsEntityCodes.Document}/${id}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data);

    EdsDebugService.logResponse(EdsEntityCodes.Document, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(normalizedResponse) : {},
    });

    yield put(getDocumentSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
    yield put(getDocumentListDataError());
  }
}

function* getDocumentWithCorrespondenceSaga({ payload: { id } }: PayloadAction<GetDocumentPayload>) {
  try {
    yield put(setDocumentWithCorrespondencesLoading(true));

    const { data } = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.Document}?includes=${DOCUMENT_WITH_CORRESPONDENCE_INCLUDES}`,
      [{ propertyName: 'Id', values: [id] }],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data.listings[0]);

    EdsDebugService.logResponse(EdsEntityCodes.Document, {
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(normalizedResponse) : {},
    });

    yield put(getDocumentWithCorrespondenceSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setDocumentWithCorrespondencesLoading(false));
  }
}

function* downloadDocumentSaga({ payload: { documentTypeName, url } }: PayloadAction<FileToDownload>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(DocumentService.downloadFile, documentTypeName, url);
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to download the file.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setSystemLoadingStatus(false));
  }
}

export function* getDocumentEntityVersionsSaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setDocumentVersionsLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/v2/${EdsEntityCodes.Document}/${payload}?includeEntities=${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.DocumentVersions}.${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      //TODO: Finish integration with EDS.
      //Currently, it is assumed that this data should not be configured (we display static data on the modals),
      //so the configuration is hardcoded there
      override: documentVersionsOverride,
    });

    const documentVersions = limitDocumentCreatedByAccounts(
      (normalizedResponse.entities?.DocumentInstances as NormalizedV2Response[]).flatMap(
        (instance) => instance.entities.DocumentVersions || [],
      ) || [],
    );

    yield put(getDocumentEntityVersionsSuccess(documentVersions));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Document versions fetching error',
      content: 'Unable to fetch document versions. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setDocumentVersionsLoading(false));
  }
}

export function* getDocumentStatusHistorySaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setDocumentStatusHistoryLoading(true));

    const { data } = yield call(
      HttpService.get,
      `audit/change-logs/${EdsEntityCodes.DocumentInstance}?entityKeys=${payload}&propertyNames=DeliveryStatusId&propertyNames=BusinessStatusId`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL,
      },
    );

    yield put(getDocumentStatusHistorySuccess(data));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Document status history fetching error',
      content: 'Unable to fetch document status history. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setDocumentStatusHistoryLoading(false));
  }
}

export function* getCorrespondenceStatusHistorySaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setCorrespondenceStatusHistoryLoading(true));

    const { data } = yield call(
      HttpService.get,
      `audit/change-logs/${EdsEntityCodes.Correspondence}?entityKeys=${payload}&propertyNames=DeliveryStatusId&propertyNames=BusinessStatusId`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL,
      },
    );

    yield put(getCorrespondenceStatusHistorySuccess(data));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Correspondence status history fetching error',
      content: 'Unable to fetch correspondence status history. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setCorrespondenceStatusHistoryLoading(false));
  }
}

export function* getDocumentMetadataSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setDocumentMetadataLoading(true));

    const { data } = yield call(
      HttpService.get,
      `Document/${payload}/metadata`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    yield put(getDocumentMetadataSuccess(data));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Document metadata fetching error',
      content: 'Unable to fetch document metadata. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setDocumentMetadataLoading(false));
  }
}

export function* systemDocumentWatcher() {
  yield takeLatest([getDocumentList.type, incrementDocumentsPage.type], getEDSDocumentListSaga);
  yield takeLatest(getDocument.type, getEDSDocumentSaga);
  yield takeLatest(getDocumentWithCorrespondence.type, getDocumentWithCorrespondenceSaga);
  yield takeLatest([getDocumentEntityVersions.type], getDocumentEntityVersionsSaga);
  yield takeLatest([getDocumentStatusHistory.type], getDocumentStatusHistorySaga);
  yield takeLatest([getCorrespondenceStatusHistory.type], getCorrespondenceStatusHistorySaga);
  yield takeLatest(downloadDocument.type, downloadDocumentSaga);
  yield takeLatest(getDocumentMetadata.type, getDocumentMetadataSaga);
}
